/**
 * @file 营销雷达卡片
 * @author alfielv
 */

import _ from 'lodash';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Loading, Empty } from '@tencent/spaui';
import { Flex, Frame } from '@tencent/odc';
import AdqIcon from '@tencent/adq-icons';
import { useResponsive } from '@tencent/od-hooks';
import classNames from 'classnames';
import { hooks } from '@tencent/ptp-fe-common';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import 'swiper/css/grid';


import { getKeyEventList } from '../../../api/home';
import { ATTENTION_EVENT_INFO } from './constants';
import MessageCard from './MessageCard';
import useAIAssistant from '../../../hooks/use-ai-assistant';

const { useFetch } = hooks;

const MessageGroupCard = () => {
  // 获取重点事件
  const { data: keyEventListResponse, fetching } = useFetch(
    getKeyEventList,
    {},
    {},
    true,
    undefined,
    (res: any) => res.data || {},
  );
  // 展示数量大于0个的重点事件
  const keyEventList = _.sortBy(
    _.filter(keyEventListResponse?.key_event_list, (item) => ATTENTION_EVENT_INFO[item?.event_type]?.enable?.(item) !== false && _.map(_.keys(ATTENTION_EVENT_INFO), _.toNumber).includes(item?.event_type)),
    (event) => {
      if (ATTENTION_EVENT_INFO[event.event_type]?.getType?.(event) === 'error') {
        return 1;
      }
      if (ATTENTION_EVENT_INFO[event.event_type]?.getType?.(event) === 'warning') {
        return 2;
      }
      return 3;
    },
  );

  const { showAIAssistant, aiAssistantModal } = useAIAssistant();
  // 重点事件操作
  const handleAction = useCallback((event_type: number, account_id_list: number[]) => {
    console.log('重点事件操作', event_type, account_id_list);
    showAIAssistant({
      defaultPrompt: ATTENTION_EVENT_INFO[event_type]?.defaultPrompt,
    });
  }, [showAIAssistant]);

  const responsive = useResponsive();

  const swiperRef = useRef<SwiperRef | null>(null);
  const handlePrev = () => {
    swiperRef.current?.swiper?.slidePrev();
  };
  const handleNext = () => {
    swiperRef.current?.swiper?.slideNext();
  };
  // 页码
  const [currentPage, setCurrentPage] = useState(1);
  // 每页卡片数
  const pageSize = useMemo(() => {
    // 大于 xl(1440) 时，展示固定245宽度的轮播,四列
    if (responsive.xl) {
      return 4;
    }
    // 大于 md(900)小于 lg(1440) 时，展示2个轮播，宽度为可用宽度
    if (responsive.md) {
      return 3;
    }
    return 2;
  }, [responsive.xl, responsive.md]);

  if (fetching) {
    return <Loading className="tw-h-[98px]" />;
  }

  // 总页数
  const totalPages = Math.ceil((keyEventList?.length ?? 0) / pageSize);
  if (totalPages < 1) {
    return <Empty size="small" type="data" title="暂无数据" className="tw-h-[98px] tw-bg-[#fafafb] tw-p-[8px] tw-text-[#898b8f]" />;
  }

  return (
    <Frame
      className={classNames({
        'w-full': !responsive.xl,
      })}
    >
      {aiAssistantModal}
      {totalPages >= 1 && (
        <>
          {/* 小于 md(900) 时，铺平依次展示 */}
          {!responsive.md && (
            <Flex column gap={12} className="w-full">
              {_.map(keyEventList, (item) => (
                <MessageCard
                  key={item.event_type}
                  data={item}
                  handleAction={handleAction}
                />
              ))}
            </Flex>
          )}

          {/* 大于 md(900)小于 xl(1440) 时，已经换行了，展示 4 列，轮播展示 */}
          {/* 大于 xl(1440) 时，展示固定宽度的的轮播 */}
          {responsive.md && (
            <Frame className='tw-flex tw-w-full tw-relative tw-gap-[8px] tw-items-center'>
              {/* 非首页时显示左箭头 */}
              {currentPage > 1 && (
                <AdqIcon
                  name="left"
                  className="cursor-pointer odc-text-secondary"
                  onClick={handlePrev}
                  style={{
                    zIndex: 10,
                  }}
                />
              )}
              <Frame style={{ width: `calc(100% - ${currentPage > 1 ? 24 : 0}px - ${currentPage < totalPages ? 24 : 0}px)` }}>
                <Swiper
                  ref={swiperRef}
                  spaceBetween={12}
                  style={{
                    transition: 'width 0.2s ease-in-out',
                    width: '100%',
                  }}
                  onSlideChange={(swiper: any) => {
                    const nowPage = swiper.activeIndex + 1;
                    setCurrentPage(nowPage);
                  }}
                >
                  {_.map(new Array(totalPages), (_item, index) => (
                    <SwiperSlide key={index}>
                      <Flex grid gap={12} style={{ gridTemplateColumns: `repeat(${pageSize}, 1fr)` }}>
                        {_.map(_.slice(keyEventList, index * pageSize, (index + 1) * pageSize), (item) => (
                          <MessageCard
                            key={item.event_type}
                            data={item}
                            handleAction={handleAction}
                          />
                        ))}
                      </Flex>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </Frame>
              {/* 非尾页时显示右箭头 */}
              {currentPage < totalPages && (
                <AdqIcon
                  name="right"
                  className="cursor-pointer odc-text-secondary"
                  onClick={handleNext}
                  style={{
                    zIndex: 10,
                  }}
                />
              )}
            </Frame>
          )}
        </>
      )}
    </Frame>
  );
};

export default MessageGroupCard;
